import { COLORS } from '@app/constants';
import {
  forwardRef,
  ForwardRefRenderFunction,
  InputHTMLAttributes,
  ReactNode,
  useId,
} from 'react';
import styled from 'styled-components';

interface SwitchProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode | string;
  className?: string;
}

const Switch: ForwardRefRenderFunction<HTMLInputElement, SwitchProps> = (
  { disabled, label, className, ...props },
  ref,
) => {
  const ownId = useId();
  const id = props.id || ownId;

  return (
    <Label className={className} htmlFor={id}>
      {label && <span>{label}</span>}
      <InputWrapper
        $isDisabled={disabled}
        ref={ref}
        type="checkbox"
        role="switch"
        id={id}
        disabled={disabled}
        {...props}
      />
    </Label>
  );
};

export default forwardRef(Switch);

interface InputWrapperProps {
  $isDisabled?: boolean;
}

const InputWrapper = styled.input<InputWrapperProps>`
  display: flex;
  align-items: center;
  box-sizing: content-box;
  width: 42px;
  height: 20px;
  padding: 2px;
  border-radius: 16px;
  background-color: ${COLORS.grayscale300};

  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};

  user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: pan-y;

  &::before {
    content: '';
    display: block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${COLORS.white};
    box-shadow: 0 0 0 0 ${COLORS.black200};
  }

  &:not(:disabled):hover::before,
  &:not(:disabled):focus::before {
    box-shadow: 0 0 0 5px ${COLORS.black200};
  }

  &:checked {
    &::before {
      transform: translateX(calc(42px - 100%));
    }
    background-color: ${COLORS.primary300};
  }

  &:disabled {
    opacity: 0.5;

    &::before {
      box-shadow: inset 0 0 0 2px hsl(0, 0%, 100% / 0.5);
    }
  }
`;

const Label = styled.label`
  cursor: pointer;

  span {
    display: flex;
    align-items: center;
  }
`;
